// Add these imports to the top of your LessonTemplate.js
import React, { useState } from 'react';
import { ChevronDown, Plus, Type, Image, Video, CreditCard, Volume2, Minus, Quote, BookOpen } from 'lucide-react';
import ApaCitationRenderer from './ContentBlocks/renderers/ApaCitationRenderer';
import ApaReferenceListRenderer from './ContentBlocks/renderers/ApaReferenceListRenderer';
import { formatInText } from './lib/apaSlideFormatter.js';

// Content Block Types Components
const TextBlock = ({ content, onUpdate, blockId }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [text, setText] = useState(content || '');

    const handleSave = () => {
        onUpdate(blockId, { type: 'text', content: text });
        setIsEditing(false);
    };

    return (
        <div className="p-4 border border-gray-200 rounded-lg bg-white">
            {isEditing ? (
                <div>
                    <textarea
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded min-h-[100px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter your text content..."
                    />
                    <div className="mt-2 flex gap-2">
                        <button
                            onClick={handleSave}
                            className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
                        >
                            Save
                        </button>
                        <button
                            onClick={() => setIsEditing(false)}
                            className="px-3 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            ) : (
                <div onClick={() => setIsEditing(true)} className="cursor-pointer min-h-[50px]">
                    {text || <span className="text-gray-400">Click to add text content...</span>}
                </div>
            )}
        </div>
    );
};

const ImageBlock = ({ content, onUpdate, blockId }) => {
    const [imageUrl, setImageUrl] = useState(content?.url || '');
    const [caption, setCaption] = useState(content?.caption || '');

    const handleUpdate = () => {
        onUpdate(blockId, {
            type: 'image',
            content: { url: imageUrl, caption }
        });
    };

    return (
        <div className="p-4 border border-gray-200 rounded-lg bg-white">
            <div className="space-y-3">
                <input
                    type="text"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    onBlur={handleUpdate}
                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter image URL..."
                />
                {imageUrl && (
                    <img
                        src={imageUrl}
                        alt={caption || 'Content image'}
                        className="max-w-full h-auto rounded"
                        onError={(e) => e.target.style.display = 'none'}
                    />
                )}
                <input
                    type="text"
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    onBlur={handleUpdate}
                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter image caption..."
                />
            </div>
        </div>
    );
};

const VideoBlock = ({ content, onUpdate, blockId }) => {
    const [videoUrl, setVideoUrl] = useState(content?.url || '');
    const [title, setTitle] = useState(content?.title || '');

    const handleUpdate = () => {
        onUpdate(blockId, {
            type: 'video',
            content: { url: videoUrl, title }
        });
    };

    return (
        <div className="p-4 border border-gray-200 rounded-lg bg-white">
            <div className="space-y-3">
                <input
                    type="text"
                    value={videoUrl}
                    onChange={(e) => setVideoUrl(e.target.value)}
                    onBlur={handleUpdate}
                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter video URL (YouTube, Vimeo, etc.)..."
                />
                <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    onBlur={handleUpdate}
                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter video title..."
                />
                {videoUrl && (
                    <div className="aspect-video bg-gray-100 rounded flex items-center justify-center">
                        <div className="text-center">
                            <Video className="mx-auto mb-2 text-gray-400" size={48} />
                            <p className="text-sm text-gray-600">Video: {title || 'Untitled'}</p>
                            <p className="text-xs text-gray-500 mt-1">URL: {videoUrl}</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const CardBlock = ({ content, onUpdate, blockId }) => {
    const [cardTitle, setCardTitle] = useState(content?.title || '');
    const [cardContent, setCardContent] = useState(content?.content || '');
    const [cardType, setCardType] = useState(content?.cardType || 'info');

    const handleUpdate = () => {
        onUpdate(blockId, {
            type: 'card',
            content: { title: cardTitle, content: cardContent, cardType }
        });
    };

    const cardStyles = {
        info: 'border-blue-200 bg-blue-50',
        warning: 'border-yellow-200 bg-yellow-50',
        success: 'border-green-200 bg-green-50',
        error: 'border-red-200 bg-red-50'
    };

    return (
        <div className={`p-4 border rounded-lg ${cardStyles[cardType]}`}>
            <div className="space-y-3">
                <select
                    value={cardType}
                    onChange={(e) => setCardType(e.target.value)}
                    onBlur={handleUpdate}
                    className="p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                    <option value="info">Info Card</option>
                    <option value="warning">Warning Card</option>
                    <option value="success">Success Card</option>
                    <option value="error">Error Card</option>
                </select>
                <input
                    type="text"
                    value={cardTitle}
                    onChange={(e) => setCardTitle(e.target.value)}
                    onBlur={handleUpdate}
                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter card title..."
                />
                <textarea
                    value={cardContent}
                    onChange={(e) => setCardContent(e.target.value)}
                    onBlur={handleUpdate}
                    className="w-full p-2 border border-gray-300 rounded min-h-[80px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter card content..."
                />
            </div>
        </div>
    );
};

const AudioBlock = ({ content, onUpdate, blockId }) => {
    const [audioUrl, setAudioUrl] = useState(content?.url || '');
    const [title, setTitle] = useState(content?.title || '');

    const handleUpdate = () => {
        onUpdate(blockId, {
            type: 'audio',
            content: { url: audioUrl, title }
        });
    };

    return (
        <div className="p-4 border border-gray-200 rounded-lg bg-white">
            <div className="space-y-3">
                <input
                    type="text"
                    value={audioUrl}
                    onChange={(e) => setAudioUrl(e.target.value)}
                    onBlur={handleUpdate}
                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter audio URL..."
                />
                <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    onBlur={handleUpdate}
                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter audio title..."
                />
                {audioUrl && (
                    <div className="p-4 bg-gray-100 rounded flex items-center space-x-3">
                        <Volume2 className="text-gray-400" size={24} />
                        <div>
                            <p className="font-medium">{title || 'Untitled Audio'}</p>
                            <p className="text-sm text-gray-600">URL: {audioUrl}</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const DividerBlock = ({ content, onUpdate, blockId, isEditMode }) => {
    const [style, setStyle] = useState(content?.style || 'solid');
    const [thickness, setThickness] = useState(content?.thickness || 2);
    const [color, setColor] = useState(content?.color || '#d1d5db');
    const [spacing, setSpacing] = useState(content?.spacing || 'medium');
    const [width, setWidth] = useState(content?.width || 'full');
    const [showOptions, setShowOptions] = useState(true);

    const handleUpdate = () => {
        onUpdate(blockId, {
            type: 'divider',
            content: { style, thickness, color, spacing, width }
        });
    };

    const spacingClasses = {
        small: 'my-4',
        medium: 'my-8',
        large: 'my-12'
    };

    const widthClasses = {
        quarter: 'w-1/4',
        half: 'w-1/2',
        'three-quarters': 'w-3/4',
        full: 'w-full'
    };

    const preview = (
        <div className={`${spacingClasses[spacing]} flex justify-center`} aria-hidden="true">
            <hr className={`${widthClasses[width]} border-0`} style={{ borderTop: `${thickness}px ${style} ${color}`, margin: '0 auto' }} />
        </div>
    );

    if (!isEditMode) {
        return preview;
    }

    return (
        <div className="p-4 border border-gray-200 rounded-lg bg-white">
            {preview}
            <button
                type="button"
                onClick={() => setShowOptions(!showOptions)}
                className="mt-2 mb-4 flex items-center text-sm text-blue-600 hover:underline"
            >
                <ChevronDown className={`mr-1 h-4 w-4 transition-transform ${showOptions ? 'rotate-180' : ''}`} />
                {showOptions ? 'Hide options' : 'Show options'}
            </button>
            {showOptions && (
                <div className="space-y-4">
                    {/* Style Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Line Style
                        </label>
                        <select
                            value={style}
                            onChange={(e) => setStyle(e.target.value)}
                            onBlur={handleUpdate}
                            className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="solid">Solid</option>
                            <option value="dashed">Dashed</option>
                            <option value="dotted">Dotted</option>
                            <option value="double">Double</option>
                        </select>
                    </div>

                    {/* Thickness Control */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Thickness ({thickness}px)
                        </label>
                        <input
                            type="range"
                            min="1"
                            max="10"
                            value={thickness}
                            onChange={(e) => setThickness(parseInt(e.target.value))}
                            onMouseUp={handleUpdate}
                            className="w-full"
                        />
                    </div>

                    {/* Color Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Color
                        </label>
                        <div className="flex space-x-2">
                            <input
                                type="color"
                                value={color}
                                onChange={(e) => setColor(e.target.value)}
                                onBlur={handleUpdate}
                                className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
                            />
                            <input
                                type="text"
                                value={color}
                                onChange={(e) => setColor(e.target.value)}
                                onBlur={handleUpdate}
                                className="flex-1 p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="#d1d5db"
                            />
                        </div>
                    </div>

                    {/* Spacing Control */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Spacing
                        </label>
                        <select
                            value={spacing}
                            onChange={(e) => setSpacing(e.target.value)}
                            onBlur={handleUpdate}
                            className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="small">Small (16px)</option>
                            <option value="medium">Medium (32px)</option>
                            <option value="large">Large (48px)</option>
                        </select>
                    </div>

                    {/* Width Control */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Width
                        </label>
                        <select
                            value={width}
                            onChange={(e) => setWidth(e.target.value)}
                            onBlur={handleUpdate}
                            className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="quarter">25%</option>
                            <option value="half">50%</option>
                            <option value="three-quarters">75%</option>
                            <option value="full">100%</option>
                        </select>
                    </div>
                </div>
            )}
        </div>
    );
};

const ApaQuoteBlock = ({ content, onUpdate, blockId }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [quoteText, setQuoteText] = useState(content?.quoteText || '');
    const [sourceType, setSourceType] = useState(content?.sourceType || 'quote');
    const [author, setAuthor] = useState(content?.author || '');
    const [year, setYear] = useState(content?.year || '');
    const [title, setTitle] = useState(content?.title || '');
    const [source, setSource] = useState(content?.source || '');
    const [url, setUrl] = useState(content?.url || '');
    const [doi, setDoi] = useState(content?.doi || '');
    const [page, setPage] = useState(content?.page || '');

    const handleSave = () => {
        onUpdate(blockId, {
            type: 'apa-quote',
            content: {
                quoteText,
                sourceType,
                author,
                year,
                title,
                source,
                url,
                doi,
                page
            }
        });
        setIsModalOpen(false);
    };

    const citationText = formatInText({
        sourceType,
        author,
        year,
        page
    });
    const cleanedUrl = url?.trim();
    const cleanedDoi = doi?.trim();
    const citationLink = cleanedDoi ? `https://doi.org/${cleanedDoi}` : cleanedUrl;
    const formattedCitation = citationText
        ? `${citationText}${/[.!?]$/.test(citationText.trim()) ? '' : '.'}`
        : '';
    const strippedQuote = String(quoteText || '').replace(/^"|"$/g, '').trim();
    const quoteBody = strippedQuote
        ? (sourceType === 'paraphrase' ? strippedQuote : `“${strippedQuote}”`)
        : 'Add your quote or paraphrase text to highlight an APA citation.';

    return (
        <div className="rounded-2xl border border-indigo-200 bg-indigo-50/60 p-5 shadow-sm">
            <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                    <span className="inline-flex items-center gap-2 rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-indigo-700">
                        <Quote size={14} />
                        APA Quote/Paraphrase
                    </span>
                    <blockquote className="mt-4 text-lg italic text-slate-700">
                        {quoteBody}
                    </blockquote>
                    <div className="mt-4 text-sm text-slate-600">
                        {formattedCitation || 'Add author, year, and source details to generate the APA citation.'}
                        {citationLink && (
                            <a
                                href={citationLink}
                                target="_blank"
                                rel="noreferrer"
                                className="ml-2 text-indigo-600 hover:text-indigo-800 hover:underline"
                            >
                                {citationLink}
                            </a>
                        )}
                    </div>
                </div>
                <button
                    type="button"
                    onClick={() => setIsModalOpen(true)}
                    className="rounded-full border border-indigo-200 bg-white px-4 py-2 text-xs font-semibold text-indigo-700 shadow-sm transition hover:border-indigo-300 hover:bg-indigo-100"
                >
                    Edit APA details
                </button>
            </div>

            {isModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center">
                    <div
                        className="absolute inset-0 bg-slate-900/50"
                        onClick={() => setIsModalOpen(false)}
                        aria-hidden="true"
                    />
                    <div
                        className="relative z-10 w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl"
                        role="dialog"
                        aria-modal="true"
                        aria-label="APA Quote editor"
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">APA Citation</p>
                                <h3 className="text-xl font-semibold text-slate-900">Quote & source details</h3>
                            </div>
                            <button
                                type="button"
                                onClick={() => setIsModalOpen(false)}
                                className="rounded-full border border-gray-200 px-3 py-1 text-sm font-semibold text-gray-600 hover:bg-gray-100"
                            >
                                Close
                            </button>
                        </div>
                        <div className="mt-6 space-y-4">
                            <div>
                                <label className="text-sm font-semibold text-gray-700">Quote or paraphrase text</label>
                                <textarea
                                    value={quoteText}
                                    onChange={(e) => setQuoteText(e.target.value)}
                                    className="mt-2 w-full rounded-lg border border-gray-300 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    placeholder="Paste the quote or paraphrase you want to highlight..."
                                    rows={4}
                                />
                            </div>
                            <label className="flex items-center gap-2 text-sm text-gray-700">
                                <input
                                    type="checkbox"
                                    checked={sourceType === 'paraphrase'}
                                    onChange={(e) => setSourceType(e.target.checked ? 'paraphrase' : 'quote')}
                                />
                                This is a paraphrase (no quotation marks)
                            </label>
                            <div className="grid gap-4 md:grid-cols-2">
                                <div>
                                    <label className="text-sm font-semibold text-gray-700">Author(s)</label>
                                    <input
                                        type="text"
                                        value={author}
                                        onChange={(e) => setAuthor(e.target.value)}
                                        className="mt-2 w-full rounded-lg border border-gray-300 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        placeholder="Author, A. A."
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700">Year</label>
                                    <input
                                        type="text"
                                        value={year}
                                        onChange={(e) => setYear(e.target.value)}
                                        className="mt-2 w-full rounded-lg border border-gray-300 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        placeholder="2024"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700">Title</label>
                                    <input
                                        type="text"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        className="mt-2 w-full rounded-lg border border-gray-300 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        placeholder="Title of the article or book"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700">Source/Publisher</label>
                                    <input
                                        type="text"
                                        value={source}
                                        onChange={(e) => setSource(e.target.value)}
                                        className="mt-2 w-full rounded-lg border border-gray-300 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        placeholder="Journal name, publisher, etc."
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700">DOI</label>
                                    <input
                                        type="text"
                                        value={doi}
                                        onChange={(e) => setDoi(e.target.value)}
                                        className="mt-2 w-full rounded-lg border border-gray-300 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        placeholder="10.0000/xyz123"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700">URL</label>
                                    <input
                                        type="text"
                                        value={url}
                                        onChange={(e) => setUrl(e.target.value)}
                                        className="mt-2 w-full rounded-lg border border-gray-300 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        placeholder="https://example.com"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="text-sm font-semibold text-gray-700">Page or paragraph</label>
                                    <input
                                        type="text"
                                        value={page}
                                        onChange={(e) => setPage(e.target.value)}
                                        className="mt-2 w-full rounded-lg border border-gray-300 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        placeholder="p. 23 or para. 4"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="mt-6 flex items-center justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => setIsModalOpen(false)}
                                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSave}
                                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
                            >
                                Save APA block
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Dropdown Menu Component
const AddContentDropdown = ({ onAddBlock, blockIndex }) => {
    const [isOpen, setIsOpen] = useState(false);

    const blockTypes = [
        { type: 'text', label: 'Text Block', icon: Type },
        { type: 'image', label: 'Image Block', icon: Image },
        { type: 'video', label: 'Video Block', icon: Video },
        { type: 'card', label: 'Card Block', icon: CreditCard },
        { type: 'audio', label: 'Audio Block', icon: Volume2 },
        { type: 'divider', label: 'Line Divider', icon: Minus },
        { type: 'apa-quote', label: 'APA Quote', icon: Quote },
        { type: 'apa-citation', label: 'Citation', icon: BookOpen },
        { type: 'apa-reference-list', label: 'Reference List', icon: BookOpen }
    ];

    const handleAddBlock = (blockType) => {
        onAddBlock(blockType, blockIndex);
        setIsOpen(false);
    };

    return (
        <div className="relative">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center space-x-1 px-3 py-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors shadow-lg"
                title="Add content block"
            >
                <Plus size={16} />
                <ChevronDown size={16} />
            </button>

            {isOpen && (
                <div className="absolute left-0 top-full mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                    <div className="py-1">
                        {blockTypes.map(({ type, label, icon: Icon }) => (
                            <button
                                key={type}
                                onClick={() => handleAddBlock(type)}
                                className="w-full flex items-center space-x-3 px-4 py-2 text-left hover:bg-gray-100 transition-colors"
                            >
                                <Icon size={16} className="text-gray-600" />
                                <span className="text-sm">{label}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Overlay to close dropdown when clicking outside */}
            {isOpen && (
                <div
                    className="fixed inset-0 z-40"
                    onClick={() => setIsOpen(false)}
                />
            )}
        </div>
    );
};

// Content Block Wrapper Component
const ContentBlockWrapper = ({ block, blockIndex, onUpdateBlock, onAddBlock }) => {
    const renderBlock = () => {
        switch (block.type) {
            case 'text':
                return (
                    <TextBlock
                        content={block.content}
                        onUpdate={onUpdateBlock}
                        blockId={block.id}
                    />
                );
            case 'image':
                return (
                    <ImageBlock
                        content={block.content}
                        onUpdate={onUpdateBlock}
                        blockId={block.id}
                    />
                );
            case 'video':
                return (
                    <VideoBlock
                        content={block.content}
                        onUpdate={onUpdateBlock}
                        blockId={block.id}
                    />
                );
            case 'card':
                return (
                    <CardBlock
                        content={block.content}
                        onUpdate={onUpdateBlock}
                        blockId={block.id}
                    />
                );
            case 'audio':
                return (
                    <AudioBlock
                        content={block.content}
                        onUpdate={onUpdateBlock}
                        blockId={block.id}
                    />
                );
            case 'divider':
                return (
                    <DividerBlock
                        content={block.content}
                        onUpdate={onUpdateBlock}
                        blockId={block.id}
                        isEditMode
                    />
                );
            case 'apa-quote':
                return (
                    <ApaQuoteBlock
                        content={block.content}
                        onUpdate={onUpdateBlock}
                        blockId={block.id}
                    />
                );
            case 'apa-citation': {
                const citationBlock = {
                    ...block,
                    citations: block.content?.citations || block.citations || []
                };
                return <ApaCitationRenderer block={citationBlock} />;
            }
            case 'apa-reference-list': {
                return <ApaReferenceListRenderer block={block} />;
            }
            default:
                return <div>Unknown block type</div>;
        }
    };

    return (
        <div className="relative group mb-6">
            <div className="flex items-start space-x-4">
                <div className="flex-1">
                    {renderBlock()}
                </div>
                <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                    <AddContentDropdown
                        onAddBlock={onAddBlock}
                        blockIndex={blockIndex}
                    />
                </div>
            </div>
        </div>
    );
};

// Main Hook for managing content blocks
const useContentBlocks = (initialBlocks = []) => {
    const [blocks, setBlocks] = useState(initialBlocks);

    const generateId = () => `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const addBlock = (blockType, afterIndex) => {
        const apaQuoteDefaults = {
            quoteText: '',
            sourceType: 'quote',
            author: '',
            year: '',
            title: '',
            source: '',
            url: '',
            doi: '',
            page: ''
        };
        const apaCitationDefaults = {
            citations: [
                {
                    type: 'book',
                    sourceType: 'paraphrase',
                    author: '',
                    year: '',
                    date: '',
                    title: '',
                    source: '',
                    url: '',
                    doi: '',
                    volume: '',
                    issue: '',
                    pages: '',
                    page: '',
                    license: '',
                    isAdapted: false,
                    platform: 'youtube'
                }
            ]
        };
        const apaReferenceListDefaults = {
            citations: [
                {
                    type: 'book',
                    sourceType: 'book',
                    author: '',
                    year: '',
                    date: '',
                    title: '',
                    source: '',
                    url: '',
                    doi: '',
                    volume: '',
                    issue: '',
                    pages: '',
                    page: '',
                    license: '',
                    isAdapted: false,
                    platform: 'youtube'
                }
            ]
        };
        const newBlock = {
            id: generateId(),
            type: blockType,
            content: blockType === 'text'
              ? ''
              : blockType === 'apa-quote'
                ? apaQuoteDefaults
                : blockType === 'apa-citation'
                  ? apaCitationDefaults
                  : blockType === 'apa-reference-list'
                    ? apaReferenceListDefaults
                  : {}
        };

        setBlocks(prevBlocks => {
            const newBlocks = [...prevBlocks];
            newBlocks.splice(afterIndex + 1, 0, newBlock);
            return newBlocks;
        });
    };

    const updateBlock = (blockId, blockData) => {
        setBlocks(prevBlocks =>
            prevBlocks.map(block =>
                block.id === blockId ? { ...block, ...blockData } : block
            )
        );
    };

    const removeBlock = (blockId) => {
        setBlocks(prevBlocks => prevBlocks.filter(block => block.id !== blockId));
    };

    return {
        blocks,
        addBlock,
        updateBlock,
        removeBlock,
        setBlocks
    };
};

// Export the components and hook for use in LessonTemplate.js
export {
    ContentBlockWrapper,
    AddContentDropdown,
    useContentBlocks,
    TextBlock,
    ImageBlock,
    VideoBlock,
    CardBlock,
    AudioBlock,
    DividerBlock,
    ApaQuoteBlock
};
